/**
 * agiBack 任务接口（Swagger：`GET /tasks/{id}`，basePath `/v1`）
 * @see http://localhost:8000/swagger/index.html
 */

import { get, post } from '../request';
import {
  buildVirtualPortraitAuthHeaders,
  type VirtualPortraitRequestOptions,
} from './virtualPortrait';

const AGI_BACK_BASE_URL = process.env.REACT_APP_AGI_BACK_URL || '/proxy/agiBack';

export interface AgiBackApiResponse<T = unknown> {
  code?: number;
  message?: string;
  data?: T;
}

export interface AgiBackTaskPromptInfo {
  id?: number;
  content?: string;
  sortOrder?: number;
  taskId?: number;
  createdAt?: string;
}

export interface AgiBackMaterialInfo {
  id?: number;
  fileName?: string;
  type?: string;
  mimeType?: string;
  storagePath?: string;
  durationSec?: number;
  sizeBytes?: number;
  metaJson?: Record<string, unknown>;
}

export interface AgiBackTaskMaterialInfo {
  id?: number;
  materialId?: number;
  slotKind?: string;
  slotIndex?: number;
  material?: AgiBackMaterialInfo;
}

export interface AgiBackTaskInfo {
  id?: number;
  title?: string;
  status?: string;
  model?: string;
  resolution?: string;
  aspectRatio?: string;
  durationSec?: number;
  remoteTaskId?: string;
  resultUrl?: string;
  resultPath?: string;
  source?: string;
  errorMessage?: string;
  createdAt?: string;
  updatedAt?: string;
  /** 提交时的生成参数（含 content 中的 asset:// 引用） */
  paramsJson?: Record<string, unknown>;
  prompts?: AgiBackTaskPromptInfo[];
  materials?: AgiBackTaskMaterialInfo[];
}

export type AgiBackTaskRequestOptions = Pick<
  VirtualPortraitRequestOptions,
  'onDebug' | 'signal' | 'systemToken' | 'userId'
>;

function unwrapAgiBackData<T>(res: AgiBackApiResponse<T>): T {
  if (res.code != null && res.code !== 0) {
    throw new Error(res.message || `请求失败（code=${res.code}）`);
  }
  if (res.data === undefined || res.data === null) {
    throw new Error(res.message || '响应无 data');
  }
  return res.data;
}

export interface EraseVideoSubtitleSubmitResult {
  task_id?: string;
  taskId?: string;
  id?: string;
  request_id?: string;
  success?: boolean;
  message?: string;
  [key: string]: unknown;
}

/** 字幕擦除等工具接口：兼容根级字段与 `{ code, data }` 包体。 */
function unwrapAgiBackToolResponse<T extends Record<string, unknown>>(
  res: AgiBackApiResponse<T> | (T & AgiBackApiResponse<T>),
): T {
  const envelope = res as AgiBackApiResponse<T> & Partial<T>;
  if (envelope.code != null && envelope.code !== 0) {
    throw new Error(envelope.message || `请求失败（code=${envelope.code}）`);
  }

  const payload =
    envelope.data != null && typeof envelope.data === 'object' && !Array.isArray(envelope.data)
      ? (envelope.data as T)
      : (envelope as unknown as T);

  const success =
    typeof payload.success === 'boolean'
      ? payload.success
      : typeof envelope.success === 'boolean'
        ? envelope.success
        : undefined;
  if (success === false) {
    const msg =
      (typeof payload.message === 'string' && payload.message) ||
      (typeof envelope.message === 'string' && envelope.message) ||
      '字幕擦除任务提交失败';
    throw new Error(msg);
  }

  return payload;
}

/** 查询类工具接口：不在 `success: false` 时抛错，交由业务解析任务状态。 */
function unwrapAgiBackToolQueryResponse<T extends Record<string, unknown>>(
  res: AgiBackApiResponse<T> | (T & AgiBackApiResponse<T>),
): T {
  const envelope = res as AgiBackApiResponse<T> & Partial<T>;
  if (envelope.code != null && envelope.code !== 0) {
    throw new Error(envelope.message || `请求失败（code=${envelope.code}）`);
  }

  if (
    envelope.data != null &&
    typeof envelope.data === 'object' &&
    !Array.isArray(envelope.data)
  ) {
    return envelope.data as T;
  }

  return envelope as unknown as T;
}

export function extractEraseVideoSubtitleTaskId(
  payload: EraseVideoSubtitleSubmitResult | null | undefined,
): string | null {
  if (!payload || typeof payload !== 'object') return null;
  return resolveTaskDetailQueryId(payload.id || payload.task_id || payload.taskId);
}

function agiBackRequestConfig(options: AgiBackTaskRequestOptions) {
  const headers = buildVirtualPortraitAuthHeaders(options as VirtualPortraitRequestOptions);
  options.onDebug?.('Headers: Authorization, New-Api-User');
  return {
    baseURL: AGI_BACK_BASE_URL,
    headers,
    responseType: 'json' as const,
    ...(options.signal ? { signal: options.signal } : {}),
  };
}

function agiBackGetConfig(options: AgiBackTaskRequestOptions) {
  return agiBackRequestConfig(options);
}

/** 任务详情路径参数：使用 33dd 上游返回的 task_id（字符串） */
export function resolveTaskDetailQueryId(value: unknown): string | null {
  if (value == null) return null;
  const id = String(value).trim();
  return id.length > 0 ? id : null;
}

/**
 * GET `/v1/tasks/{id}` — 任务详情（`id` 为 33dd 任务 ID；含 prompts、materials / materialId）
 */
export const getAgiBackTaskById = async (
  taskId: number | string,
  options: AgiBackTaskRequestOptions = {},
): Promise<AgiBackTaskInfo> => {
  const id = resolveTaskDetailQueryId(taskId);
  if (id == null) {
    throw new Error('无效的任务 ID');
  }
  const pathId = encodeURIComponent(id);
  options.onDebug?.(`GET ${AGI_BACK_BASE_URL}/v1/tasks/${pathId}`);
  const res = await get<AgiBackApiResponse<AgiBackTaskInfo>>(
    `/v1/tasks/${pathId}`,
    agiBackGetConfig(options),
  );
  return unwrapAgiBackData(res);
};

/**
 * POST `/v1/tools/erase-video-subtitle` — 提交字幕擦除（标准版）任务（透传）。
 *
 * 入参为视频地址 `video_url`；响应可能是根级 `{ success, task_id }`，也可能包在 `data` 里。
 */
export const submitEraseVideoSubtitle = async (
  videoUrl: string,
  options: AgiBackTaskRequestOptions = {},
): Promise<EraseVideoSubtitleSubmitResult> => {
  const url = (videoUrl || '').trim();
  if (!url) {
    throw new Error('视频地址无效');
  }
  options.onDebug?.(`POST ${AGI_BACK_BASE_URL}/v1/tools/erase-video-subtitle`);
  options.onDebug?.('Headers: Authorization, New-Api-User');
  const res = await post<AgiBackApiResponse<EraseVideoSubtitleSubmitResult>>(
    '/v1/tools/erase-video-subtitle',
    { video_url: url },
    agiBackRequestConfig(options),
  );
  const payload = unwrapAgiBackToolResponse<EraseVideoSubtitleSubmitResult>(res);
  const taskId = extractEraseVideoSubtitleTaskId(payload);
  if (!taskId) {
    throw new Error('未获取到任务 ID');
  }
  options.onDebug?.(`字幕擦除任务已创建: ${taskId}`);
  return { ...payload, task_id: taskId };
};

export interface EraseVideoSubtitleTaskQueryResult {
  task_id?: string;
  taskId?: string;
  task_type?: string;
  status?: string;
  success?: boolean;
  message?: string;
  result?: {
    video_url?: string;
    videoUrl?: string;
    [key: string]: unknown;
  };
  error?: string | { message?: string };
  request_id?: string;
  [key: string]: unknown;
}

export type EraseVideoSubtitleTaskOutcome =
  | { kind: 'ready'; videoUrl: string }
  | { kind: 'pending' }
  | { kind: 'failed'; message: string };

const ERASE_SUBTITLE_FAILED_STATUSES = new Set([
  'failed',
  'error',
  'cancelled',
  'canceled',
  'timeout',
  'expired',
]);

function extractEraseVideoSubtitleErrorMessage(
  payload: EraseVideoSubtitleTaskQueryResult,
): string {
  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message.trim();
  }
  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error.trim();
  }
  if (payload.error && typeof payload.error === 'object') {
    const nested = (payload.error as { message?: string }).message;
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
  }
  return '擦除字幕任务失败';
}

function extractEraseVideoSubtitleResultVideoUrl(
  payload: EraseVideoSubtitleTaskQueryResult,
): string | null {
  const result =
    payload.result && typeof payload.result === 'object' ? payload.result : null;
  const candidates = [result?.video_url, result?.videoUrl, payload.video_url, payload.videoUrl];
  for (const value of candidates) {
    if (typeof value !== 'string') continue;
    const url = value.trim();
    if (url.startsWith('http')) return url;
  }
  return null;
}

export function resolveEraseVideoSubtitleTaskOutcome(
  payload: EraseVideoSubtitleTaskQueryResult | null | undefined,
): EraseVideoSubtitleTaskOutcome {
  if (!payload || typeof payload !== 'object') {
    return { kind: 'failed', message: '查询结果无效' };
  }

  if (payload.success === false) {
    return { kind: 'failed', message: extractEraseVideoSubtitleErrorMessage(payload) };
  }

  const status = String(payload.status || '').trim().toLowerCase();
  if (ERASE_SUBTITLE_FAILED_STATUSES.has(status)) {
    return { kind: 'failed', message: extractEraseVideoSubtitleErrorMessage(payload) };
  }

  const videoUrl = extractEraseVideoSubtitleResultVideoUrl(payload);
  if (videoUrl) {
    return { kind: 'ready', videoUrl };
  }

  return { kind: 'pending' };
}

/**
 * GET `/v1/ark-tasks/{task_id}` — 查询字幕擦除任务结果。
 *
 * 响应可能是根级 `{ success, status, result: { video_url } }`，也可能包在 `data` 里。
 */
export const getEraseVideoSubtitleTaskResult = async (
  taskId: number | string,
  options: AgiBackTaskRequestOptions = {},
): Promise<EraseVideoSubtitleTaskQueryResult> => {
  const id = resolveTaskDetailQueryId(taskId);
  if (id == null) {
    throw new Error('无效的任务 ID');
  }
  const pathId = encodeURIComponent(id);
  options.onDebug?.(`GET ${AGI_BACK_BASE_URL}/v1/ark-tasks/${pathId}`);
  const res = await get<AgiBackApiResponse<EraseVideoSubtitleTaskQueryResult>>(
    `/v1/ark-tasks/${pathId}`,
    agiBackGetConfig(options),
  );
  return unwrapAgiBackToolQueryResponse<EraseVideoSubtitleTaskQueryResult>(res);
};
